import { apiRequest } from '../../shared/api';
import {
  HelpCategory,
  HelpFaq,
  SupportTicket,
  SupportTicketMessage,
  HelpCenterOverviewKpi,
} from './types';

function getAuthHeaders() {
  const token = localStorage.getItem('auth_token') || '';
  const userStr = localStorage.getItem('auth_user');
  let orgId = 'ORG-XYZ';
  let userId = '';
  let userRole = 'USER';

  if (userStr) {
    try {
      const u = JSON.parse(userStr);
      orgId = u.organization?.id || u.organization?.code || orgId;
      userId = u.id || '';
      userRole = u.user_type || 'USER';
    } catch (e) {}
  }

  return {
    Authorization: `Bearer ${token}`,
    'x-organization-id': orgId,
    'x-user-id': userId,
    'x-user-role': userRole,
    'x-is-system-admin': userRole === 'SYSTEM_ADMIN' ? 'true' : 'false',
  };
}

export const helpService = {
  // Overview KPIs
  async getOverview(): Promise<HelpCenterOverviewKpi> {
    return apiRequest<HelpCenterOverviewKpi>('/help/overview', {
      headers: getAuthHeaders(),
    });
  },

  // Categories
  async getCategories(): Promise<HelpCategory[]> {
    return apiRequest<HelpCategory[]>('/help/categories', {
      headers: getAuthHeaders(),
    });
  },

  async createCategory(data: Partial<HelpCategory>): Promise<HelpCategory> {
    return apiRequest<HelpCategory>('/help/categories', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  // FAQs
  async getFaqs(params?: { category_id?: string; search?: string }): Promise<HelpFaq[]> {
    const query = new URLSearchParams();
    if (params?.category_id) query.set('category_id', params.category_id);
    if (params?.search) query.set('search', params.search);
    const qs = query.toString() ? `?${query.toString()}` : '';

    return apiRequest<HelpFaq[]>(`/help/faqs${qs}`, {
      headers: getAuthHeaders(),
    });
  },

  async getFaqById(id: string): Promise<HelpFaq> {
    return apiRequest<HelpFaq>(`/help/faqs/${id}`, {
      headers: getAuthHeaders(),
    });
  },

  async createFaq(data: {
    question: string;
    answer: string;
    category_id?: string | null;
    tags?: string;
    is_global: boolean;
    organization_id?: string | null;
    created_by_name?: string;
  }): Promise<HelpFaq> {
    return apiRequest<HelpFaq>('/help/faqs', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  async markHelpful(id: string): Promise<void> {
    await apiRequest(`/help/faqs/${id}/helpful`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
  },

  // Tickets
  async getTickets(params?: { filter_org_id?: string; status?: string; category?: string }): Promise<SupportTicket[]> {
    const query = new URLSearchParams();
    if (params?.filter_org_id) query.set('filter_org_id', params.filter_org_id);
    if (params?.status) query.set('status', params.status);
    if (params?.category) query.set('category', params.category);
    const qs = query.toString() ? `?${query.toString()}` : '';

    return apiRequest<SupportTicket[]>(`/help/tickets${qs}`, {
      headers: getAuthHeaders(),
    });
  },

  async getTicketById(id: string): Promise<SupportTicket> {
    return apiRequest<SupportTicket>(`/help/tickets/${id}`, {
      headers: getAuthHeaders(),
    });
  },

  async createTicket(data: {
    subject: string;
    description: string;
    category: string;
    priority: string;
    requester_name?: string;
    requester_email?: string;
  }): Promise<SupportTicket> {
    return apiRequest<SupportTicket>('/help/tickets', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
  },

  async addTicketMessage(ticketId: string, message: string, isInternalNote = false): Promise<SupportTicketMessage> {
    return apiRequest<SupportTicketMessage>(`/help/tickets/${ticketId}/messages`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        message,
        is_internal_note: isInternalNote,
      }),
    });
  },

  async updateTicketStatus(ticketId: string, status: string, resolutionNotes?: string): Promise<SupportTicket> {
    return apiRequest<SupportTicket>(`/help/tickets/${ticketId}/status`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        status,
        resolution_notes: resolutionNotes,
      }),
    });
  },

  // Organizations helper for Admin selector
  async getOrganizations(): Promise<Array<{ id: string; organization_code: string; legal_name: string }>> {
    return apiRequest<Array<{ id: string; organization_code: string; legal_name: string }>>('/auth/organizations', {
      headers: getAuthHeaders(),
    });
  },
};
